// src/components/Finanzas/utils/timeBoundaries.js
// Date boundaries for the Finanzas module, in the tenant's timezone.
//
// The finanzas views already return fecha_emision / fecha_cobro as SV calendar
// dates, so every range here is a pair of inclusive 'YYYY-MM-DD' strings meant
// for .gte() / .lte() on those date columns — no timestamp conversion client-side.
//
// El Salvador has no DST, but nothing here hardcodes an offset: "today" and
// timestamp display go through Intl with the zone name, so a tenant in a DST
// zone only needs TIME_ZONE changed. Pure date-string arithmetic runs in UTC,
// where a calendar day is always exactly one day.

export const TIME_ZONE = 'America/El_Salvador';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad2 = (n) => String(n).padStart(2, '0');

const dateParts = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const dateTimeParts = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const partsOf = (fmt, date) => {
  const out = {};
  for (const p of fmt.formatToParts(date)) out[p.type] = p.value;
  return out;
};

// 'YYYY-MM-DD' → { y, m, d } (m is 1-based), or null if malformed.
const parseDateStr = (dateStr) => {
  const match = DATE_RE.exec(String(dateStr || '').slice(0, 10));
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
};

// UTC-normalised date string; Date.UTC rolls day/month overflow for us.
const utcDateStr = (y, m, d) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);

/** Calendar date of an instant, as seen in El Salvador → 'YYYY-MM-DD'. */
export function svDateStr(date = new Date()) {
  const p = partsOf(dateParts, date instanceof Date ? date : new Date(date));
  return `${p.year}-${p.month}-${p.day}`;
}

/** Today in El Salvador → 'YYYY-MM-DD'. */
export function svTodayStr() {
  return svDateStr(new Date());
}

/** Shift a 'YYYY-MM-DD' by n calendar days (n may be negative). */
export function addDaysStr(dateStr, n) {
  const p = parseDateStr(dateStr);
  if (!p) return null;
  return utcDateStr(p.y, p.m, p.d + n);
}

/** Month containing dateStr, e.g. { startStr: '2026-09-01', endStr: '2026-09-30', label: 'Septiembre 2026' }. */
export function calendarMonthRange(dateStr) {
  const p = parseDateStr(dateStr) || parseDateStr(svTodayStr());
  return {
    startStr: utcDateStr(p.y, p.m, 1),
    endStr:   utcDateStr(p.y, p.m + 1, 0), // day 0 of next month = last day of this one
    label:    `${MESES[p.m - 1]} ${p.y}`,
  };
}

/** The `days` days ending on dateStr, both ends inclusive. */
export function rollingRange(dateStr, days) {
  const n = Math.max(1, Math.floor(Number(days) || 1));
  const endStr = parseDateStr(dateStr) ? String(dateStr).slice(0, 10) : svTodayStr();
  return {
    startStr: addDaysStr(endStr, -(n - 1)),
    endStr,
    label: n === 1 ? 'Hoy' : `Últimos ${n} días`,
  };
}

/** Operator-picked range. Bounds are swapped if entered backwards. */
export function customRange(fromStr, toStr) {
  let startStr = String(fromStr || '').slice(0, 10);
  let endStr   = String(toStr || '').slice(0, 10);
  if (!parseDateStr(startStr)) startStr = svTodayStr();
  if (!parseDateStr(endStr))   endStr   = startStr;
  if (startStr > endStr) [startStr, endStr] = [endStr, startStr];
  return {
    startStr,
    endStr,
    label: startStr === endStr
      ? formatSvDate(startStr)
      : `${formatSvDate(startStr)} – ${formatSvDate(endStr)}`,
  };
}

/** The last n calendar months including the current one, newest first. */
export function lastNMonths(n) {
  const today = parseDateStr(svTodayStr());
  const count = Math.max(1, Math.floor(Number(n) || 1));
  const out = [];
  for (let i = 0; i < count; i += 1) {
    out.push(calendarMonthRange(utcDateStr(today.y, today.m - i, 1)));
  }
  return out;
}

/** The last n days ending today (SV), both ends inclusive. */
export function lastNDays(n) {
  return rollingRange(svTodayStr(), n);
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY'. The input is already an SV date, so no zone shift. */
export function formatSvDate(dateStr) {
  const p = parseDateStr(dateStr);
  if (!p) return '—';
  return `${pad2(p.d)}/${pad2(p.m)}/${p.y}`;
}

/** ISO instant → 'DD/MM/YYYY HH:MM' in El Salvador time. */
export function formatSvDateTime(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const p = partsOf(dateTimeParts, date);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}
